import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageFrame, PageHeader, Surface, FilterBar, Pill, ActiveStatus, RowActionMenu, Button as UiButton } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { ClientApprovalListItem } from '@/types/client';
import { clientApprovalAPI } from '@/services/api';
import { formatClientName } from '@/utils/formatters';

import {
  ArrowBack as ArrowBackIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';

// 3. Main Component
const PendingApprovalsList: React.FC = () => {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState<ClientApprovalListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientApprovalAPI.getApprovals({ approval_status: 'PENDING' });
      setApprovals(response.results || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch pending approvals');
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  // Memoized Filter Logic
  const filteredApprovals = useMemo(() => {
    if (!searchTerm) return approvals;
    const lowerSearch = searchTerm.toLowerCase();

    return approvals.filter((approval) => {
      const d = approval.object_details || {};
      const name = (d.full_name || `${d.first_name} ${d.last_name}`).toLowerCase();
      return (
        (d.client_code?.toLowerCase() || '').includes(lowerSearch) ||
        name.includes(lowerSearch) ||
        (d.email?.toLowerCase() || '').includes(lowerSearch) ||
        (d.pan_card?.toLowerCase() || '').includes(lowerSearch)
      );
    });
  }, [approvals, searchTerm]);

  // Handlers
  const handleReview = useCallback((approval: ClientApprovalListItem) => {
    navigate(`/client-approvals/${approval.id}`);
  }, [navigate]);

  /* Columns replace <ApprovalRow>. Status and change type were hand-coloured
     MUI Chips with their own hex map; they are <Pill>s now, so an approval
     reads the same as a status anywhere else. */
  const columns = useMemo<ColumnDef<ClientApprovalListItem>[]>(() => {
    const CHANGE_TONE: Record<string, { label: string; tone: 'info' | 'neutral' | 'positive' | 'critical' }> = {
      CREATE: { label: 'New Client', tone: 'info' },
      UPDATE: { label: 'Update', tone: 'neutral' },
      ACTIVATE: { label: 'Activation', tone: 'positive' },
      DEACTIVATE: { label: 'Deactivation', tone: 'critical' },
    };
    return [
      { key: 'client_code', header: 'Client Code', width: '140px',
        render: r => r.object_details?.client_code || '—' },
      { key: 'name', header: 'Name', width: '220px',
        render: r => (formatClientName(r.object_details || {}) || '').toUpperCase() || '—' },
      { key: 'email', header: 'Email', width: '220px',
        render: r => (r.object_details?.email || '').toUpperCase() || '—' },
      { key: 'mobile', header: 'Mobile', width: '150px',
        render: r => r.object_details?.contact_no || r.object_details?.mobile || '—' },
      { key: 'fa_account_no', header: 'FA Account No', width: '170px',
        render: r => r.object_details?.fa_account_no || r.object_details?.account_no || '—' },
      { key: 'scheme', header: 'Scheme', width: '170px',
        render: r => {
          const d = r.object_details || {};
          if (!d.scheme) return '—';
          return typeof d.scheme === 'object' ? d.scheme.name || '—' : d.scheme_name || '—';
        } },
      { key: 'is_nri', header: 'NRI', width: '90px',
        render: r => {
          const v = r.object_details?.is_nri;
          if (v === true || String(v) === 'true') return 'Yes';
          if (v === false || String(v) === 'false') return 'No';
          return '—';
        } },
      { key: 'status', header: 'Status', width: '130px',
        render: r => <ActiveStatus active={!!r.object_details?.is_active} /> },
      { key: 'action_type', header: 'Change Type', width: '150px',
        render: r => {
          const c = CHANGE_TONE[r.action_type || 'UPDATE'] || CHANGE_TONE.UPDATE;
          return <Pill tone={c.tone}>{c.label}</Pill>;
        } },
      { key: 'actions', header: 'Action', width: '80px',
        render: r => (
          <RowActionMenu
            label={`Actions for ${r.object_details?.client_code || 'approval'}`}
            actions={[
              {
                label: 'Review Client',
                icon: <RateReviewIcon fontSize="small" />,
                onClick: () => handleReview(r),
              },
            ]}
          />
        ) },
    ];
  }, [handleReview]);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Pending Approvals"
          subtitle="Client changes waiting for review before they take effect."
          actions={
            <UiButton onClick={() => navigate('/clients')} icon={<ArrowBackIcon fontSize="small" />}>
              Back to Clients
            </UiButton>
          }
        />
      }
    >
      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by code, name, email or PAN…"
      />

      <Surface>
        <DataTable<ClientApprovalListItem>
          embedded
          data={filteredApprovals}
          columns={columns}
          rowKey={row => row.id}
          totalCount={filteredApprovals.length}
          loading={loading}
          error={error}
          currentPage={1}
          totalPages={1}
          onPageChange={() => undefined}
          pageSize={filteredApprovals.length || 1}
          sortField=""
          sortDir="asc"
          onSort={() => undefined}
          hideSearch
          loadingMessage="Loading approvals…"
          emptyMessage={searchTerm ? 'No approvals match that search.' : 'All caught up — nothing is waiting for review.'}
        />
      </Surface>

    </PageFrame>
  );
};

export default PendingApprovalsList;